import {
  FACHLICHE_ZEITZONE,
  IsoWeekError,
  formatIsoWeekKey,
  parseIsoWeek,
} from '@asas/domain/iso-week';
import type {
  AnfrageAblehnenInput,
  AnfrageAusgang,
  AnfrageEntschieden,
  AnfrageFreigebenInput,
  AnfrageVerweis,
  AnfrageVorschauInput,
  AnfrageZurueckziehenInput,
  IsoWeek,
  Permission,
  RessourcenAnfrage,
  RessourcenAnfrageErgebnis,
  RessourcenAnfrageInput,
} from '@asas/domain';

import type { FehlerBefund } from '../daten/fehler';
import { stundenAnzeige } from '../wochenraster/wochenraster';

/**
 * Reine Hilfen fuer die anonyme Ressourcenanfrage (AC-404) — ohne React, ohne
 * Netz. Pflichtfelder, Verteilung und Hinweise prueft der Server
 * (`@asas/domain/ressourcen-anfrage.ts`); die Oberflaeche wandelt nur die
 * Eingabe um und zeigt, was zurueckkommt. Stunden bleiben Zeichenketten
 * (Kernregel 10).
 */

/** Das Recht hinter `anfragen.anlegen`, `.meine` und `.wunschKandidaten`. */
export const ANFRAGE_RECHT: Permission = 'resource_allocation:request';
/** Das Recht hinter `anfragen.offene` (AK-3). */
export const ANFRAGEN_LESERECHT: Permission = 'resource_allocation:approve';

/** Bietet die Ansicht „Ressourcen anfragen" an? Sonst schriebe der Klick ein 403 ins Audit-Log. */
export function darfRessourcenAnfragen(rechte: ReadonlySet<Permission>): boolean {
  return rechte.has(ANFRAGE_RECHT);
}

/** Bietet das Wochenraster die offenen Anfragen der Fachgruppe an? */
export function darfOffeneAnfragenLesen(rechte: ReadonlySet<Permission>): boolean {
  return rechte.has(ANFRAGEN_LESERECHT);
}

/** Was im Formular steht — Wochen als Wert eines `<input type="week">` (`2026-W50`). */
export interface AnfrageFormular {
  readonly fachgruppeId: string;
  readonly stunden: string;
  readonly von: string;
  readonly bis: string;
  /** `''` heisst „kein Wunsch". */
  readonly wunschEmployeeId: string;
}

export function leeresFormular(fachgruppeId: string): AnfrageFormular {
  return { fachgruppeId, stunden: '', von: '', bis: '', wunschEmployeeId: '' };
}

export type FormularFehler = 'fachgruppe' | 'stunden' | 'von' | 'bis';

const FORMULAR_MELDUNG: Readonly<Record<FormularFehler, string>> = {
  fachgruppe: 'Bitte eine Fachgruppe wählen.',
  stunden: 'Bitte den Stundenumfang angeben.',
  von: 'Bitte die erste Kalenderwoche angeben.',
  bis: 'Bitte die letzte Kalenderwoche angeben.',
};

export function formularMeldung(fehler: FormularFehler): string {
  return FORMULAR_MELDUNG[fehler];
}

function woche(wert: string): IsoWeek | null {
  try {
    return parseIsoWeek(wert);
  } catch (error) {
    if (error instanceof IsoWeekError) return null;
    throw error;
  }
}

/**
 * Formular → Eingabe der Prozedur, oder das erste fehlende Pflichtfeld (AK-1).
 * „12,5" wird „12.5"; ob die Zahl taugt, sagt der Server.
 */
export function anfrageEingabe(
  formular: AnfrageFormular,
  bezug: { readonly projectId: string; readonly workPackageId: string },
  umfangBestaetigt: boolean,
): RessourcenAnfrageInput | { readonly fehlt: FormularFehler } {
  if (formular.fachgruppeId === '') return { fehlt: 'fachgruppe' };

  const stunden = formular.stunden.trim().replace(',', '.');
  if (stunden === '') return { fehlt: 'stunden' };

  const von = woche(formular.von);
  if (von === null) return { fehlt: 'von' };
  const bis = woche(formular.bis);
  if (bis === null) return { fehlt: 'bis' };

  return {
    projectId: bezug.projectId,
    workPackageId: bezug.workPackageId,
    fachgruppeId: formular.fachgruppeId,
    stunden,
    von,
    bis,
    wunschEmployeeId: formular.wunschEmployeeId === '' ? null : formular.wunschEmployeeId,
    ...(umfangBestaetigt ? { umfangBestaetigt: true } : {}),
  };
}

/** „KW 50/2026 – KW 1/2027"; eine einzelne Woche ohne Strich. */
export function wochenbereichText(von: IsoWeek, bis: IsoWeek): string {
  const kw = (w: IsoWeek) => `KW ${String(w.isoWeek)}/${String(w.isoYear)}`;

  return von.isoYear === bis.isoYear && von.isoWeek === bis.isoWeek
    ? kw(von)
    : `${kw(von)} – ${kw(bis)}`;
}

/** AK-6: der Hinweis auf die bestehende Anfrage — mit Zeitraum und Umfang. */
export function duplikatText(bestehende: readonly AnfrageVerweis[]): string {
  const liste = bestehende
    .map((a) => `${wochenbereichText(a.von, a.bis)} (${stundenAnzeige(a.stunden)} h)`)
    .join('; ');

  return `Für dieses Arbeitspaket und diese Fachgruppe ist im gewählten Zeitraum schon angefragt: ${liste}. Es wurde keine zweite Anfrage angelegt.`;
}

/** AK-7: die Rückfrage vor einem großen Umfang. */
export function umfangText(stunden: string, schwelleStunden: number): string {
  return `${stundenAnzeige(stunden.replace(',', '.'))} h sind mehr als ${String(schwelleStunden)} h. Ist der Umfang richtig (kein Zahlendreher)?`;
}

export type AnfrageHinweisAnzeige = Exclude<RessourcenAnfrageErgebnis, { ergebnis: 'angelegt' }>;

const WARNUNG_UNGEWISS =
  ' Möglicherweise wurde bereits angefragt – bitte „Meine Anfragen" laden, bevor Sie es erneut versuchen.';

/** Meldung zu einem gescheiterten Anfragen; den Code liest nur `klassifiziereFehler`. */
export function meldungFuerAnfrage(befund: FehlerBefund): string {
  let meldung = befund.meldung;

  if (befund.art === 'eingabe') {
    meldung =
      befund.felder.length === 0
        ? 'Bitte Fachgruppe, Stunden und Kalenderwochen prüfen.'
        : befund.felder.map((feld) => feld.message).join(' ');
  } else if (befund.art === 'nicht_gefunden') {
    meldung =
      'Projekt, Arbeitspaket, Fachgruppe oder Wunschperson gibt es nicht mehr; bitte neu laden.';
  } else if (befund.art === 'konflikt') {
    meldung = 'Für dieses Arbeitspaket wurde gerade angefragt; bitte „Meine Anfragen" laden.';
  }

  return befund.moeglicherweiseAusgefuehrt ? `${meldung}${WARNUNG_UNGEWISS}` : meldung;
}

// --- Anfrage entscheiden (AC-405) -------------------------------------------------

/**
 * Bietet die offene Anfrage „Entscheiden" an? Die Prozeduren verlangen
 * `resource_allocation:approve`; die Personenauswahl liest die Mitglieder der
 * Fachgruppe (`fachgruppen.get`, `fachgruppe:read`). Ohne beides kein Knopf —
 * sonst schriebe der Klick ein 403 ins Audit-Log.
 */
export function darfEntscheiden(rechte: ReadonlySet<Permission>): boolean {
  return rechte.has(ANFRAGEN_LESERECHT) && rechte.has('fachgruppe:read');
}

/** Bietet „Meine Anfragen" das Zurückziehen an? Dasselbe Recht wie das Anfragen. */
export function darfZurueckziehen(rechte: ReadonlySet<Permission>): boolean {
  return rechte.has(ANFRAGE_RECHT);
}

/** Eine Woche im Entscheidungsformular — die Woche als Wert eines `<input type="week">`. */
export interface EntscheidungsWoche {
  readonly woche: string;
  readonly stunden: string;
}

export interface EntscheidungsFormular {
  /** `''` heisst „noch keine Person gewählt". */
  readonly employeeId: string;
  readonly wochen: readonly EntscheidungsWoche[];
  readonly begruendung: string;
}

/**
 * Das Formular, wie die Anfrage gestellt wurde: die angefragten Wochen mit
 * ihren Stunden und — falls genannt — der Wunsch als Vorbelegung. Der Wunsch
 * ist unverbindlich; abgeschickt wird, was im Formular steht (E2).
 */
export function entscheidungsFormular(anfrage: RessourcenAnfrage): EntscheidungsFormular {
  return {
    employeeId: anfrage.wunsch?.employeeId ?? '',
    wochen: anfrage.wochen.map((w) => ({
      woche: formatIsoWeekKey(w.woche),
      stunden: stundenAnzeige(w.planHours),
    })),
    begruendung: '',
  };
}

export type EntscheidungsFehler = 'person' | 'wochen' | 'stunden' | 'begruendung';

const ENTSCHEIDUNG_MELDUNG: Readonly<Record<EntscheidungsFehler, string>> = {
  person: 'Bitte eine Person aus der Fachgruppe wählen.',
  wochen: 'Bitte mindestens eine gültige Kalenderwoche angeben, jede höchstens einmal.',
  stunden: 'Bitte für jede Woche die Stunden angeben.',
  begruendung: 'Eine Ablehnung braucht eine Begründung.',
};

export function entscheidungsMeldung(fehler: EntscheidungsFehler): string {
  return ENTSCHEIDUNG_MELDUNG[fehler];
}

/** Die Version jeder Wochenzeile, wie sie geladen wurde (Kernregel 32). */
export function versionenVon(anfrage: RessourcenAnfrage): AnfrageZurueckziehenInput['versionen'] {
  return anfrage.wochen.map(({ allocationId, version }) => ({ allocationId, version }));
}

/** Formular → Eingabe der Vorschau, oder das erste fehlende Feld. „12,5" wird „12.5". */
export function vorschauEingabe(
  formular: EntscheidungsFormular,
  anfrage: RessourcenAnfrage,
): AnfrageVorschauInput | { readonly fehlt: EntscheidungsFehler } {
  if (formular.employeeId === '') return { fehlt: 'person' };
  if (formular.wochen.length === 0) return { fehlt: 'wochen' };

  const wochen: { woche: IsoWeek; stunden: string }[] = [];
  for (const eintrag of formular.wochen) {
    const w = woche(eintrag.woche);
    if (w === null || wochen.some((x) => formatIsoWeekKey(x.woche) === formatIsoWeekKey(w))) {
      return { fehlt: 'wochen' };
    }
    const stunden = eintrag.stunden.trim().replace(',', '.');
    if (stunden === '') return { fehlt: 'stunden' };
    wochen.push({ woche: w, stunden });
  }

  return { anfrageId: anfrage.anfrageId, employeeId: formular.employeeId, wochen };
}

/** Formular → Eingabe der Freigabe; `ueberlastBestaetigt` beantwortet die Warnung (AK 4). */
export function freigabeEingabe(
  formular: EntscheidungsFormular,
  anfrage: RessourcenAnfrage,
  ueberlastBestaetigt: boolean,
): AnfrageFreigebenInput | { readonly fehlt: EntscheidungsFehler } {
  const vorschau = vorschauEingabe(formular, anfrage);
  if ('fehlt' in vorschau) return vorschau;

  return {
    ...vorschau,
    versionen: versionenVon(anfrage),
    ...(ueberlastBestaetigt ? { ueberlastBestaetigt: true } : {}),
  };
}

/** Formular → Eingabe der Ablehnung; ohne Begründung kein Aufruf (AK 3). */
export function ablehnungEingabe(
  formular: EntscheidungsFormular,
  anfrage: RessourcenAnfrage,
): AnfrageAblehnenInput | { readonly fehlt: EntscheidungsFehler } {
  const begruendung = formular.begruendung.trim();
  if (begruendung === '') return { fehlt: 'begruendung' };

  return { anfrageId: anfrage.anfrageId, versionen: versionenVon(anfrage), begruendung };
}

const ZEITPUNKT = new Intl.DateTimeFormat('de-DE', {
  timeZone: FACHLICHE_ZEITZONE,
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

/** `2026-10-23T08:15:00.000Z` → „23.10.2026, 10:15" in der fachlichen Zeitzone. */
export function zeitpunktText(zeitstempel: string): string {
  return ZEITPUNKT.format(new Date(zeitstempel));
}

/** „KW 46/2026, KW 48/2026" — die Wochen der Überlastwarnung (AK 4). */
export function wochenText(wochen: readonly IsoWeek[]): string {
  return wochen.map((w) => wochenbereichText(w, w)).join(', ');
}

const ERGEBNIS_TEXT = {
  freigegeben: 'freigegeben',
  abgelehnt: 'abgelehnt',
  zurueckgezogen: 'zurückgezogen',
} as const;

/**
 * AK 5, AK 6: Die Anfrage ist schon entschieden — wer, wann, wie. Ein
 * Hinweis, kein Fehler im Sinne von „etwas ist kaputt"; gespeichert wurde nichts.
 */
export function entschiedenText(entschieden: AnfrageEntschieden): string {
  const wer = entschieden.entschiedenVon.name ?? 'einem anderen Konto';

  return `Diese Anfrage wurde inzwischen ${ERGEBNIS_TEXT[entschieden.ergebnis]} – am ${zeitpunktText(entschieden.entschiedenAm)} von ${wer}. Ihre Entscheidung wurde nicht gespeichert.`;
}

const ABWEICHUNG_TEXT = { person: 'Person', wochen: 'Wochen', stunden: 'Stunden' } as const;

/**
 * Der Ausgang für die Projektleitung (AK 2, AK 3): wer eingesetzt wurde,
 * worin abgewichen wurde, oder die Begründung der Ablehnung.
 */
export function ausgangText(ausgang: AnfrageAusgang): string {
  const wann = zeitpunktText(ausgang.entschiedenAm);

  if (ausgang.ergebnis === 'abgelehnt') {
    return `Abgelehnt am ${wann}: ${ausgang.begruendung ?? ''}`;
  }

  const an = ausgang.person === null ? '' : ` an ${ausgang.person.name}`;
  const abweichung =
    ausgang.abweichung.length === 0
      ? 'wie angefragt'
      : `abweichend bei ${ausgang.abweichung.map((g) => ABWEICHUNG_TEXT[g]).join(', ')}`;
  const ueberlast = ausgang.ueberlastBestaetigt ? ', mit bestätigter Überlast' : '';

  return `Freigegeben${an} am ${wann} – ${abweichung}${ueberlast}`;
}

/** „KW 46/2026: 20 h → 12 h" je Woche, die sich geändert hat oder neu bzw. entfallen ist. */
export function abweichungDetails(ausgang: AnfrageAusgang): string[] {
  if (ausgang.freigegeben === null) return [];

  const vorher = new Map(ausgang.angefragt.map((w) => [formatIsoWeekKey(w.woche), w]));
  const nachher = new Map(ausgang.freigegeben.map((w) => [formatIsoWeekKey(w.woche), w]));
  const schluessel = [...new Set([...vorher.keys(), ...nachher.keys()])].sort();

  return schluessel.flatMap((k) => {
    const alt = vorher.get(k);
    const neu = nachher.get(k);

    if (alt === undefined && neu !== undefined) {
      return [
        `${wochenbereichText(neu.woche, neu.woche)}: neu, ${stundenAnzeige(neu.planHours)} h`,
      ];
    }
    if (alt !== undefined && neu === undefined) {
      return [
        `${wochenbereichText(alt.woche, alt.woche)}: entfällt (angefragt ${stundenAnzeige(alt.planHours)} h)`,
      ];
    }
    if (alt === undefined || neu === undefined || alt.planHours === neu.planHours) return [];
    return [
      `${wochenbereichText(alt.woche, alt.woche)}: ${stundenAnzeige(alt.planHours)} h → ${stundenAnzeige(neu.planHours)} h`,
    ];
  });
}

/** Meldung zu einer gescheiterten Entscheidung oder einem Rückzug. */
export function meldungFuerEntscheidung(befund: FehlerBefund): string {
  let meldung = befund.meldung;

  if (befund.art === 'eingabe') {
    meldung =
      befund.felder.length === 0
        ? 'Bitte Person, Wochen und Stunden prüfen.'
        : befund.felder.map((feld) => feld.message).join(' ');
  } else if (befund.art === 'nicht_gefunden') {
    meldung = 'Die Anfrage oder die Person gibt es nicht mehr; bitte die Liste neu laden.';
  } else if (befund.art === 'konflikt') {
    meldung = 'Die Anfrage hat sich inzwischen geändert; bitte die Liste neu laden.';
  }

  return befund.moeglicherweiseAusgefuehrt
    ? `${meldung} Möglicherweise wurde bereits entschieden – bitte die Liste neu laden, bevor Sie es erneut versuchen.`
    : meldung;
}
